import React, { useEffect, useRef, useState } from 'react';
import ActionButton from '../../../../Components/Common/ActionButton';
import { TextField } from '../../../../Components/Form';
import useAsyncAction from '../../../../Components/Hooks/useAsyncAction';
import { RespondMessageBubble } from '../../../../shared/offer-domain/ui';
import notify from '../../../../utils/notify';
import pertaminaMark from '../../../../assets/images/pertamina-mark.png';
import { pick } from '../../../../shared/offer-domain/pick';
import { formatDateTime } from '../../../../utils/format';
import { copy, isBuyerLocale } from '../../utils/negotiationCopy';
import { SENDER_SIDE } from '../../utils/negotiationStatus';

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Message fields are not spelled out in be#451; read both casings.
const toMessage = m => ({
  id: pick(m, 'id', 'Id'),
  kind: Number(pick(m, 'kind', 'Kind') || 0),
  senderSide: Number(pick(m, 'senderSide', 'SenderSide')),
  senderName: pick(m, 'senderName', 'SenderName'),
  text: pick(m, 'message', 'Message') || '',
  createdDate: pick(m, 'createdDate', 'CreatedDate'),
});

// kind -> card look (CSS already in Negotiation.css).
const SYSTEM_CARD = {
  3: { mod: 'neg-system-card--deal', icon: 'ri-checkbox-circle-fill' },
  4: { mod: 'neg-system-card--withdrawn', icon: 'ri-logout-box-r-fill' },
  5: { mod: 'neg-system-card--withdrawn', icon: 'ri-alarm-warning-fill' },
};
const DEFAULT_CARD = {
  mod: 'neg-system-card--started',
  icon: 'ri-play-circle-fill',
};

const LEGAL_STOP =
  /^(PT|PTE|LTD|TBK|CO|INC|LLC|THE|AND|OF|INTERNATIONAL|TRADING)$/i;

const companyInitials = name => {
  const words = String(name || '')
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w && !LEGAL_STOP.test(w));
  if (!words.length) return 'C';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

// System events (kind != 0) carry server-written text; only the title is ours.
const SystemEventCard = ({ kind, text, meta, locale }) => {
  const card = SYSTEM_CARD[kind] || DEFAULT_CARD;
  const title = copy.systemTitle[kind]?.[locale] || 'System';

  return (
    <div className={`neg-system-card ${card.mod}`} role='status'>
      <div className='neg-system-card__icon' aria-hidden='true'>
        <i className={card.icon} />
      </div>
      <div className='neg-system-card__body'>
        <div className='neg-system-card__title'>{title}</div>
        {text ? <p className='neg-system-card__text mb-0'>{text}</p> : null}
        {meta ? <div className='neg-system-card__meta'>{meta}</div> : null}
      </div>
    </div>
  );
};

const CollaborationChatLog = ({
  messages: rawMessages = [],
  onSend,
  isSubmitting,
  readOnly = false,
  mode = 'buyer',
}) => {
  const [text, setText] = useState('');
  const logRef = useRef(null);
  const locale = isBuyerLocale(mode) ? 'id' : 'en';
  const messages = rawMessages.map(toMessage);

  useEffect(() => {
    if (!logRef.current) return;
    logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages.length]);

  // Guards the Enter key too, which bypasses the button's own pending state.
  const [handleSend, isSending] = useAsyncAction(async () => {
    if (readOnly || !text.trim() || !onSend) return;
    try {
      await onSend(text.trim());
      setText('');
    } catch (error) {
      notify.apiError(error, 'send chat');
    }
  });

  return (
    <section className='neg-panel neg-panel--chat'>
      <header className='neg-panel__head'>
        <div>
          <h3 className='neg-panel__title'>
            <i className='ri-chat-3-line' aria-hidden='true' />
            Chat
          </h3>
          <p className='neg-panel__desc'>
            {readOnly
              ? locale === 'id'
                ? 'Negosiasi selesai — percakapan tidak dapat diubah'
                : 'Negotiation finished — conversation is closed'
              : locale === 'id'
                ? 'Catatan antara panitia dan mitra'
                : 'Notes between Pertamina and the supplier'}
          </p>
        </div>
      </header>
      <div className='neg-panel__body neg-chat'>
        <div className='neg-chat__log' ref={logRef}>
          {messages.length === 0 ? (
            <div className='neg-empty'>
              <i className='ri-chat-off-line' aria-hidden='true' />
              <p className='mb-0 small'>
                {readOnly
                  ? locale === 'id'
                    ? 'Tidak ada pesan.'
                    : 'No messages recorded.'
                  : locale === 'id'
                    ? 'Belum ada pesan.'
                    : 'No messages yet.'}
              </p>
            </div>
          ) : (
            messages.map((m, idx) => {
              const meta = `${formatDateTime(m.createdDate)} WIB`;
              const key = m.id || `${m.createdDate}-${idx}`;
              if (m.kind !== 0 || m.senderSide === SENDER_SIDE.SYSTEM) {
                return (
                  <SystemEventCard
                    key={key}
                    kind={m.kind}
                    text={m.text}
                    meta={meta}
                    locale={locale}
                  />
                );
              }

              const isBuyer = m.senderSide === SENDER_SIDE.PERTAMINA;
              return (
                <RespondMessageBubble
                  key={key}
                  isEvaluator={isBuyer}
                  content={`<p class="mb-0">${escapeHtml(m.text)}</p>`}
                  authorLabel={m.senderName}
                  metaDate={meta}
                  avatarLabel={isBuyer ? 'P' : companyInitials(m.senderName)}
                  avatarSrc={isBuyer ? pertaminaMark : undefined}
                  roleLabel={isBuyer ? 'Pertamina' : 'Supplier'}
                  title={null}
                />
              );
            })
          )}
        </div>

        <div className='neg-chat__composer'>
          {readOnly ? (
            <ActionButton
              variant='secondary'
              type='button'
              icon='ri-lock-line'
              disabled
              className='w-100'
            >
              {locale === 'id' ? 'Chat ditutup' : 'Chat closed'}
            </ActionButton>
          ) : (
            <>
              <TextField
                label={locale === 'id' ? 'Pesan' : 'Message'}
                name='chatMessage'
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={
                  locale === 'id'
                    ? 'Tulis catatan singkat…'
                    : 'Write a short note…'
                }
                mode={isSubmitting || isSending ? 'disabled' : 'editable'}
                inputProps={{
                  onKeyDown: e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  },
                }}
              />
              <ActionButton
                variant='primary'
                icon='ri-send-plane-line'
                onClick={handleSend}
                loading={isSubmitting || isSending}
                disabled={isSubmitting || isSending || !text.trim()}
              >
                {locale === 'id' ? 'Kirim' : 'Send'}
              </ActionButton>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CollaborationChatLog;
